import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || !token) {
        setStatus("error");
        return;
      }

      try {
        const response = await fetch(`${API}/subscription/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.payment_status === "paid") {
            setStatus("success");
            // Refresh user data to get updated subscription status
            if (refreshUser) {
              await refreshUser();
            }
            toast.success(t('subscription.welcome_premium'));
          } else if (pollCount < 5) {
            // Continue polling
            setPollCount(prev => prev + 1);
            setTimeout(checkPaymentStatus, 2000);
          } else {
            setStatus("error");
          }
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Status check error:", error);
        setStatus("error");
      }
    };

    checkPaymentStatus();
  }, [sessionId, token, pollCount, refreshUser, t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white rounded-3xl border border-orange-100/50 p-8 text-center space-y-6">
        {status === "checking" && (
          <>
            <div className="w-20 h-20 rounded-full bg-orange-50 mx-auto flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-secondary">{t('subscription.processing_payment')}</h1>
              <p className="text-muted-foreground mt-2">{t('subscription.please_wait')}</p>
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-success/10 mx-auto flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-secondary">{t('subscription.welcome_to_premium')}</h1>
              <p className="text-muted-foreground mt-2">
                {t('subscription.trial_started')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{t('subscription.benefits_unlocked')}</span>
              </div>
              <ul className="text-sm text-secondary space-y-1">
                <li>{t('subscription.benefit_1')}</li>
                <li>{t('subscription.benefit_2')}</li>
                <li>{t('subscription.benefit_3')}</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold"
            >
              {t('subscription.go_to_dashboard')}
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/10 mx-auto flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-secondary">{t('subscription.something_wrong')}</h1>
              <p className="text-muted-foreground mt-2">
                {t('subscription.couldnt_confirm')}
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-5"
              >
                {t('subscription.go_to_dashboard')}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full rounded-full py-5"
              >
                {t('common.try_again')}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
